import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLocalization } from '../../contexts/LocalizationContext';
import userService from '../../services/userService';
import Input from '../../components/Common/Input';
import Button from '../../components/Common/Button';

const ROLES = [
  {
    value: 1,
    en: 'Employee',
    ar: 'موظف',
    descEn: 'View-only access. Can browse orders and products but cannot make changes.',
    descAr: 'صلاحية عرض فقط. يمكنه تصفح الطلبات والمنتجات دون إجراء تغييرات.',
    color: '#6b7280',
  },
  {
    value: 2,
    en: 'Order Manager',
    ar: 'مدير الطلبات',
    descEn: 'Can view and update order statuses, manage customer communications.',
    descAr: 'يمكنه عرض الطلبات وتحديث حالاتها وإدارة التواصل مع العملاء.',
    color: '#3b82f6',
  },
  {
    value: 3,
    en: 'Product Manager',
    ar: 'مدير المنتجات',
    descEn: 'Can manage products, categories, brands, advertisements, and discounts.',
    descAr: 'يمكنه إدارة المنتجات والتصنيفات والعلامات التجارية والإعلانات والخصومات.',
    color: '#f59e0b',
  },
  {
    value: 4,
    en: 'Super Admin',
    ar: 'مدير عام',
    descEn: 'Full access to everything including user management and system settings.',
    descAr: 'صلاحية كاملة على كل شيء بما في ذلك إدارة المستخدمين وإعدادات النظام.',
    color: '#ef4444',
  },
];

const CreateUser = () => {
  const { isRtl } = useLocalization();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    username: '',
    password: '',
    fullName: '',
    email: '',
    role: 1,
    isActive: true,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const selectedRole = ROLES.find(r => r.value === Number(form.role)) || ROLES[0];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.username.trim() || !form.password || !form.fullName.trim() || !form.email.trim()) {
      setError(isRtl ? 'جميع الحقول مطلوبة' : 'All fields are required');
      return;
    }
    setIsLoading(true);
    try {
      await userService.create({
        username: form.username,
        password: form.password,
        fullName: form.fullName,
        email: form.email,
        role: Number(form.role),
        isActive: form.isActive,
      });
      navigate('/admin/users');
    } catch (err) {
      setError(err.response?.data?.error || (isRtl ? 'حدث خطأ أثناء إنشاء المستخدم' : 'Error creating user'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="create-user-page">
      <div className="page-header">
        <div className="page-header-content">
          <div className="page-header-main">
            <h1 className="page-title">{isRtl ? 'إنشاء مستخدم إداري جديد' : 'Create New Admin User'}</h1>
            <p className="page-description">{isRtl ? 'إضافة مسؤول جديد إلى النظام' : 'Add a new administrator to the system'}</p>
          </div>
          <div className="page-header-actions">
            <Button variant="secondary" onClick={() => navigate('/admin/users')}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M19 12H5M12 19l-7-7 7-7"/>
              </svg>
              {isRtl ? 'العودة إلى المستخدمين' : 'Back to Users'}
            </Button>
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--sp-6)', alignItems: 'start' }}>
        {/* Left — form */}
        <div className="card">
          <div className="card-header">
            <span style={{ fontWeight: 700, fontSize: 'var(--fs-md)' }}>
              {isRtl ? 'بيانات المستخدم' : 'User Details'}
            </span>
          </div>
          <div className="card-body">
            {error && (
              <div className="alert alert-error" style={{ marginBottom: 'var(--sp-4)' }}>{error}</div>
            )}
            <form onSubmit={handleSubmit}>
              <Input
                label={isRtl ? 'اسم المستخدم *' : 'Username *'}
                value={form.username}
                onChange={e => setForm({ ...form, username: e.target.value })}
                required
              />
              <Input
                label={isRtl ? 'كلمة المرور *' : 'Password *'}
                type="password"
                value={form.password}
                onChange={e => setForm({ ...form, password: e.target.value })}
                required
              />
              <Input
                label={isRtl ? 'الاسم الكامل *' : 'Full Name *'}
                value={form.fullName}
                onChange={e => setForm({ ...form, fullName: e.target.value })}
                required
              />
              <Input
                label={isRtl ? 'البريد الإلكتروني *' : 'Email *'}
                type="email"
                value={form.email}
                onChange={e => setForm({ ...form, email: e.target.value })}
                required
              />

              <div className="form-group">
                <label className="form-label">{isRtl ? 'الدور' : 'Role'}</label>
                <select
                  className="form-select"
                  value={form.role}
                  onChange={e => setForm({ ...form, role: Number(e.target.value) })}
                >
                  {ROLES.map(r => (
                    <option key={r.value} value={r.value}>{isRtl ? r.ar : r.en}</option>
                  ))}
                </select>
              </div>

              {/* Selected role preview */}
              <div style={{
                padding: 'var(--sp-3) var(--sp-4)',
                borderRadius: 'var(--r-lg)',
                background: selectedRole.color + '12',
                border: `1.5px solid ${selectedRole.color}44`,
                marginBottom: 'var(--sp-4)',
              }}>
                <div style={{ fontWeight: 700, color: selectedRole.color, fontSize: 'var(--fs-sm)', marginBottom: 4 }}>
                  {isRtl ? selectedRole.ar : selectedRole.en}
                </div>
                <div style={{ fontSize: '12px', color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                  {isRtl ? selectedRole.descAr : selectedRole.descEn}
                </div>
              </div>

              <div className="form-group">
                <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={form.isActive}
                    onChange={e => setForm({ ...form, isActive: e.target.checked })}
                  />
                  {isRtl ? 'الحساب نشط' : 'Account Active'}
                </label>
              </div>

              <div style={{ display: 'flex', gap: 'var(--sp-3)', justifyContent: 'flex-end', marginTop: 'var(--sp-5)' }}>
                <Button variant="secondary" type="button" onClick={() => navigate('/admin/users')}>
                  {isRtl ? 'إلغاء' : 'Cancel'}
                </Button>
                <Button variant="primary" type="submit" disabled={isLoading}>
                  {isLoading ? (isRtl ? 'جاري الحفظ...' : 'Saving...') : (isRtl ? 'إنشاء المستخدم' : 'Create User')}
                </Button>
              </div>
            </form>
          </div>
        </div>

        {/* Right — role reference */}
        <div className="card">
          <div className="card-header">
            <span style={{ fontWeight: 700, fontSize: 'var(--fs-md)' }}>
              {isRtl ? 'صلاحيات الأدوار' : 'Role Permissions'}
            </span>
          </div>
          <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
            {ROLES.map(r => {
              const active = Number(form.role) === r.value;
              return (
                <button
                  key={r.value}
                  type="button"
                  onClick={() => setForm({ ...form, role: r.value })}
                  style={{
                    textAlign: isRtl ? 'right' : 'left',
                    padding: 'var(--sp-4)',
                    borderRadius: 'var(--r-lg)',
                    border: `2px solid ${active ? r.color : 'var(--border)'}`,
                    background: active ? r.color + '12' : 'var(--surface)',
                    cursor: 'pointer',
                    transition: 'all 0.18s ease',
                    width: '100%',
                  }}
                >
                  <div style={{ fontWeight: 700, fontSize: 'var(--fs-sm)', color: active ? r.color : 'var(--text)', marginBottom: 6 }}>
                    {isRtl ? r.ar : r.en}
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                    {isRtl ? r.descAr : r.descEn}
                  </div>
                </button>
              );
            })}
            <p style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: 'var(--sp-1)' }}>
              {isRtl ? '* انقر على الدور لتحديده مباشرة' : '* Click a role to select it directly'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateUser;
